/**
 * A group combination in a week view day cell (#1579 part 3):
 * "07:00-08:30 + Grizzly" on the host row, "07:00-08:30 with Panda" on a joining row.
 */

import { useLingui } from '@lingui/react';
import { Users } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { GroupCombination } from '@/types/groupCombinations';
import type { CombinationRole } from './combinationLayout';

export interface CombinationChipProps {
  combination: GroupCombination;
  role: CombinationRole;
  onClick?: (combination: GroupCombination) => void;
}

export function CombinationChip({ combination, role, onClick }: CombinationChipProps) {
  const { i18n } = useLingui();
  const time = `${combination.start_time}-${combination.end_time}`;
  const label =
    role === 'host'
      ? i18n._({
          id: 'shifts.combination.chip.host',
          message: '{time} + {members}',
          values: { time, members: combination.member_group_names.join(' + ') },
        })
      : i18n._({
          id: 'shifts.combination.chip.member',
          message: '{time} with {host}',
          values: { time, host: combination.host_group_name },
        });

  const className = cn(
    'flex w-full items-center gap-1 rounded-md border border-dashed border-primary/60 bg-primary/10 px-1.5 py-0.5 text-left text-xs text-foreground',
    onClick && 'cursor-pointer hover:bg-primary/20'
  );

  const content = (
    <>
      <Users className="h-3 w-3 shrink-0" aria-hidden="true" />
      <span className="truncate">{label}</span>
    </>
  );

  if (!onClick) {
    return (
      <div className={className} data-testid="combination-chip" title={label}>
        {content}
      </div>
    );
  }
  return (
    <button
      type="button"
      className={className}
      data-testid="combination-chip"
      title={label}
      onClick={(e) => {
        e.stopPropagation();
        onClick(combination);
      }}
    >
      {content}
    </button>
  );
}
